import { Box, TextField } from '@mui/material';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import useIssueReport from '../../hooks/issueReport/useIssueReport.hooks';
import { ISSUE_DETAIL_CHAR_CAP } from '../../redux/features/issueReport/issueReport.slice';
import {
  IssueClassifySurface,
  IssueReportRecord,
} from '../../redux/features/issueReport/issueReport.props';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import IssueTypeChips from './IssueTypeChips';

export interface IssueClassifyHandle {
  /**
   * Drop the pending detail so the unmount commit does not re-send it.
   *
   * `Discard` retracts the whole report (`voidIssue`), and the host owns that
   * dispatch — but the "has this already been committed?" latch lives in here with
   * the text it guards, so the host has to say so imperatively.
   */
  discard(): void;
}

interface IssueClassifyBodyProps {
  /**
   * The report being classified. Everything the body needs is read from the RECORD,
   * never from props: the report is already filed by the time any host mounts this,
   * so the record is the one source for what it carries.
   */
  record: IssueReportRecord;
  /**
   * Which surface this is mounted in, emitted as `classified_from` when a chip is
   * clicked. REQUIRED, deliberately: it is the only way the reversal's per-surface
   * conversion is measurable, and a host that forgets it would silently emit nulls
   * that read as "nobody classified this".
   */
  surface: IssueClassifySurface;
  /**
   * A classification gesture finished — the host should close itself.
   *
   * Fires on a chip click and on Enter. Note that Enter fires it even when nothing
   * was sent (an untouched seed): Enter's contract is "always closes, only sends when
   * there is something new", so this means "done with me", not "a write happened".
   */
  onCommitted?: () => void;
  /**
   * The FIRST keystroke of a typing episode, re-armed on blur.
   *
   * Deliberately not `onFocus`. The field is focused automatically when a host opens,
   * so pausing on focus meant the countdown never ran and every panel lived to the
   * 60s hard cap. Per-keystroke would be a second clock racing the first, and the dial
   * would stutter as the agent thinks.
   */
  onDetailFirstInput?: () => void;
  /** Every blur. The flag panel resumes its clock from the banked remainder. */
  onDetailBlur?: () => void;
  /** Take the caret on mount, with it placed after the seed. */
  autoFocusDetail?: boolean;
  /**
   * Whether Enter in the detail field commits. The flag panel: true. Backlog
   * surfaces: false — there is no 10s window there to race, so the only reason to
   * bind a sending key is absent.
   */
  enterSubmits?: boolean;
}

/**
 * HITL-1572: the classify surface — a detail field and the whole taxonomy — with the
 * record-shaped logic that three different hosts would otherwise each reimplement.
 *
 * Split from `IssueFlagPanel` along one line: *is this about the record, or about the
 * 10-second window?* Everything here is about the record, so it holds for a backlog
 * row with no window at all. The window — the dial, pause/resume, the window-level
 * Escape, the caret handback, `Discard` — stays with the panel.
 *
 * ## The four things a host must not have to re-derive
 *
 * 1. **Seed handling.** A word click files a half-written sentence; the field opens
 *    with it, and an UNCHANGED seed must never go out as a second revision.
 * 2. **Type preservation.** A detail-only commit must not erase a type the filing
 *    gesture already chose.
 * 3. **Commit on unmount.** The surface can vanish from under the agent — the panel's
 *    clock runs out, a task auto-assigns off the lobby — and the typed detail is the
 *    only signal saying what the taxonomy is missing.
 * 4. **What Enter does, and what it attributes.** See `handleDetailKeyDown`.
 *
 * Getting any of them subtly wrong is silent: a report goes out with a sentence the
 * agent never typed, or loses a classification, or drops the text entirely.
 *
 * HITL-1576 added `surface`, so an amend says WHERE the agent classified from. Only a
 * chip click carries it — a detail is not a triage decision.
 */
const IssueClassifyBody = React.forwardRef<
  IssueClassifyHandle,
  IssueClassifyBodyProps
>(
  (
    {
      record,
      surface,
      onCommitted,
      onDetailFirstInput,
      onDetailBlur,
      autoFocusDetail = false,
      enterSubmits = false,
    },
    ref
  ) => {
    const { commitIssueType } = useIssueReport();
    const [detail, setDetail] = useState('');
    const detailRef = useRef<HTMLInputElement | null>(null);

    // The detail the agent typed must survive the unmount that closing causes, so the
    // cleanup commit reads a ref rather than state torn down mid-effect.
    const detailValueRef = useRef('');
    detailValueRef.current = detail;
    /**
     * Whether the host has already been told about this typing episode. Re-armed on
     * blur, so a blur-then-type pauses the clock again rather than leaving it running
     * while the agent writes.
     */
    const firstInputSignalledRef = useRef(false);
    // Set once a chip has already committed this record's detail, so the unmount
    // cleanup does not send it a second time at the next revision. Also what
    // `discard()` sets.
    const committedRef = useRef(false);
    /**
     * The detail this surface OPENED with, when the gesture that filed it seeded one
     * (the word-click prefills `"burger" should be `).
     *
     * Held so the unmount commit can tell a seed the agent never finished from text
     * they actually wrote. Without it every timed-out word-click would emit a second
     * revision carrying a sentence the agent did not type.
     */
    const seededDetailRef = useRef('');
    /**
     * The type the record already carries, read by the unmount commit and by Enter.
     *
     * That commit sends a typed detail with the type alongside it, and `reportAmended`
     * assigns the type unconditionally — so passing null there would ERASE a
     * classification the gesture had already made. A word click files
     * `Incorrect Transcription`; without this, finishing the sentence wiped it.
     *
     * Captured when the record OPENS, not during render: a host can null its active
     * report as it closes, so the last render before unmount may have no record to
     * read and a render-time assignment would put back the null this exists to
     * prevent. A chip click cannot be missed either — it sets `committedRef` and the
     * cleanup returns before reaching this.
     */
    const activeTypeRef = useRef<string | null>(null);

    useImperativeHandle(
      ref,
      () => ({
        discard: () => {
          committedRef.current = true;
        },
      }),
      []
    );

    /**
     * One record, one detail — and the cleanup is also where a detail the agent typed
     * but never committed gets recorded.
     *
     * Keyed on the record id ALONE. Re-running when the record's own `issue_detail`
     * changes would reset the field out from under the agent on every amend. Reset in
     * the effect rather than relying on unmount, because a host may keep this mounted
     * across records.
     */
    useEffect(() => {
      // A trailing space is added for CONTINUATION: the record stores the sanitized
      // sentence (`"burger" should be`, trimmed for the wire), but the agent is meant
      // to type the next word onto the end of it, and the caret must not land flush
      // against `be`. The unchanged-seed comparison below trims both sides, so this
      // cannot make an untouched seed look edited.
      const stored = record.issue_detail ?? '';
      const seed = stored ? `${stored} ` : '';
      seededDetailRef.current = seed;
      activeTypeRef.current = record.issue_type ?? null;
      setDetail(seed);
      committedRef.current = false;
      firstInputSignalledRef.current = false;

      // Focused explicitly rather than via the `autoFocus` prop, and on a timeout: the
      // field has to exist and the surrounding DOM settle before it can take the caret.
      // The timeout is also what puts this AFTER the click handback that opened it.
      const focusTimer = autoFocusDetail
        ? setTimeout(() => {
            const field = detailRef.current;
            if (!field) return;
            field.focus();
            // Caret AFTER the seed, so the agent completes the sentence rather than
            // landing in front of it or having to press End first. An unseeded surface
            // puts it at 0, which is the same thing.
            const end = field.value.length;
            field.setSelectionRange(end, end);
          }, 0)
        : undefined;

      return () => {
        if (focusTimer) clearTimeout(focusTimer);
        const typed = detailValueRef.current.trim();
        if (!typed || committedRef.current) return;
        // Unchanged seed is not agent input. Compared trimmed, because the seed ends
        // in a space and `sanitizeIssueDetail` would trim it away anyway — so an
        // untouched prefill and its sanitized form must compare equal.
        if (typed === seededDetailRef.current.trim()) return;
        commitIssueType({
          issue_id: record.issue_id,
          // Preserved, not nulled — see activeTypeRef.
          issue_type: activeTypeRef.current,
          issue_detail: typed,
          // The surface is going away without the agent choosing a type, so nothing
          // was classified here. Attributing this would count an abandoned
          // classification as a completed one.
          classified_from: null,
        });
      };
      // commitIssueType is stable across renders (useCallback in useIssueReport).
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [record.issue_id]);

    /**
     * Every chip behaves identically — commit and close, detail optional. There is no
     * blocking branch anywhere, including for `Not listed`: a confirm step buys nothing
     * when re-classifying is just another revision.
     */
    const handleSelect = useCallback(
      (issueType: string) => {
        committedRef.current = true;
        commitIssueType({
          issue_id: record.issue_id,
          issue_type: issueType,
          issue_detail: detailValueRef.current.trim() || null,
          // The ONE place a surface is attributed: a chip click is the agent making a
          // triage decision, which is the thing worth counting per surface.
          classified_from: surface,
        });
        // The thunk deliberately does not close anything — it is also used by the
        // timeout path, where the window has already gone. Closing is the host's job,
        // and a chip click must close IMMEDIATELY rather than wait out a countdown.
        onCommitted?.();
      },
      [commitIssueType, onCommitted, record.issue_id, surface]
    );

    const handleDetailChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (!firstInputSignalledRef.current) {
          firstInputSignalledRef.current = true;
          onDetailFirstInput?.();
        }
        setDetail(e.target.value);
      },
      [onDetailFirstInput]
    );

    const handleDetailBlur = useCallback(() => {
      firstInputSignalledRef.current = false;
      onDetailBlur?.();
    }, [onDetailBlur]);

    /**
     * Enter commits the typed detail and closes. One key, one result, every report.
     *
     * ## It used to be gated on the two ASR types, and HITL-1576 removed that
     *
     * The gate existed because on an action-bar flag no type has been chosen yet, so
     * an Enter pressed out of habit committed `issue_type: null` — and `Unclassified`
     * was TERMINAL. A reflex keystroke permanently destroyed the classification on the
     * most common path, which is about as bad as a single keypress can be.
     *
     * HITL-1571 made a null-typed commit RECOVERABLE: it is exactly what puts a report
     * in the outstanding backlog, where the agent picks it up from the Issue button or
     * the task-router lobby. The mistake the gate prevented no longer exists, so the
     * gate is gone — and with it the surprise of a key that silently did nothing on the
     * most common report while working on the rarest.
     *
     * The ordering was not a convenience: relaxing this before those surfaces existed
     * would have let a reflex keystroke commit a null type with nowhere to resurface.
     *
     * ## Kept exactly as it was
     *
     * The untouched-seed check. Revision 0 already carries a word click's seeded
     * sentence, so Enter on an unfinished prefill closes without emitting a revision
     * identical to the one before it. Enter always CLOSES; it only SENDS when there is
     * something new. And a mid-composition Enter still commits the IME candidate rather
     * than the report.
     *
     * `activeTypeRef`, not a re-derivation: a detail-only commit must carry whatever
     * type the record already has, or `reportAmended` would assign null over it.
     *
     * ## With a chip selected, Enter IS pressing that chip
     *
     * So it attributes the surface, and the report leaves the backlog confirmed. This
     * first shipped attributing nothing on the grounds that "Enter sends a detail, not
     * a classification" — true only when no type is selected. On a word click the chip
     * is pre-selected from the machine's guess, and QA found the consequence: the agent
     * typed a detail, pressed Enter, the panel sent the type, and the report was STILL
     * listed as needing classification. Enter closes the panel and sends the type; a
     * gesture that does both cannot also mean "I have not decided".
     *
     * With NO type selected it still attributes nothing, which on the panel means an
     * action-bar flag: the typed sentence is all there is, and counting it as a triage
     * decision would inflate exactly the number `classified_from` exists to measure. A
     * chip click commits and closes immediately, so a type can only be active here
     * because the report arrived pre-typed from a transcript gesture.
     *
     * The untouched-seed case is deliberately NOT confirmation. A word click seeds an
     * unfinished sentence, so Enter on a prefill nobody has touched is as likely to be
     * a reflex to get back to the omnibar as it is a decision — and the ambiguous
     * gesture takes the recoverable outcome: the panel closes and the report stays in
     * the backlog.
     */
    const handleDetailKeyDown = useCallback(
      // Typed against the TextField's ROOT, which MUI renders as a div.
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (!enterSubmits) return;
        if (e.key !== 'Enter') return;
        // Mid-composition Enter commits the IME candidate, not the report.
        if (
          (e.nativeEvent as unknown as { isComposing?: boolean }).isComposing
        ) {
          return;
        }
        e.preventDefault();

        const typed = detailValueRef.current.trim();
        if (typed && typed !== seededDetailRef.current.trim()) {
          // Preserved, not re-derived — see activeTypeRef.
          const issueType = activeTypeRef.current;
          committedRef.current = true;
          commitIssueType({
            issue_id: record.issue_id,
            issue_type: issueType,
            issue_detail: typed,
            // Submitting WITH a type selected is accepting that type — see above.
            // With none, this is a detail and nothing more.
            classified_from: issueType ? surface : null,
          });
        }
        onCommitted?.();
      },
      [commitIssueType, enterSubmits, onCommitted, record.issue_id, surface]
    );

    return (
      <Box data-testid="issue-classify-body">
        {/* ABOVE the chips: with one global escape hatch this is the only thing
            that says WHAT the taxonomy missed, and the flow reads top-to-bottom —
            type the context, then click the type that sends it. */}
        <TextField
          inputRef={detailRef}
          value={detail}
          onChange={handleDetailChange}
          onKeyDown={handleDetailKeyDown}
          // Typing PAUSES the host's countdown; blur resumes from the banked
          // remainder. Not "typing extends": extension has no exit when an agent
          // is pulled into an order mid-sentence.
          onBlur={handleDetailBlur}
          placeholder="Add a few more details (optional)"
          fullWidth
          size="small"
          inputProps={{ maxLength: ISSUE_DETAIL_CHAR_CAP }}
          sx={{
            '& .MuiInputBase-root': {
              backgroundColor: 'rgba(255,255,255,0.08)',
              color: Colors.white,
              fontSize: HITL3_FONT_SIZE,
            },
            '& fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
            // A clear focused border: this field takes the caret on open, so the
            // agent must be able to see at a glance where their keystrokes are
            // going — the omnibar is inches away.
            '& .Mui-focused fieldset': {
              borderColor: `${Colors.green} !important`,
              borderWidth: '2px !important',
            },
          }}
        />

        {/* No height cap and no scroll: all 23 chips are meant to be readable in one
            glance, and a scrollbar would hide part of the taxonomy behind a gesture —
            which is the exact failure (a value buried below the fold) this exists to
            fix.

            One row per category is part of that: a wrapped row reads as two groups and
            costs the height a scrollbar would have saved. */}
        <Box sx={{ mt: 1.5 }}>
          <IssueTypeChips
            onSelect={handleSelect}
            selectedValue={record.issue_type}
          />
        </Box>
      </Box>
    );
  }
);

IssueClassifyBody.displayName = 'IssueClassifyBody';

export default IssueClassifyBody;
